import { IconButton } from '@hyperflake/react-ui-library';
import moment from 'moment';
import React, { MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const usePTZDelayedEventHandlers = (params: { delay: number }) => {
    const { delay } = params;

    const mouseDownTimeRef = useRef<moment.Moment | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleStart = useCallback(async (fn: () => void | Promise<void>) => {
        // Record the time when mouse is pressed
        mouseDownTimeRef.current = moment();

        // Call start endpoint
        console.log('Calling start endpoint');

        await fn();
    }, []);

    const handleEnd = useCallback(
        async (fn: () => void | Promise<void>) => {
            if (mouseDownTimeRef.current === null) return;

            // Calculate how long the button was held (in milliseconds)
            const heldDuration = moment().diff(mouseDownTimeRef.current);

            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            if (heldDuration >= delay) {
                await fn();
            } else {
                const remainingTime = 2000 - heldDuration;
                timeoutRef.current = setTimeout(async () => {
                    await fn();
                }, remainingTime);
            }

            // Reset the mouse down time
            mouseDownTimeRef.current = null;
        },
        [delay]
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return { onStart: handleStart, onEnd: handleEnd };
};

interface IPtzButtonProps {
    children: ReactNode;
    className?: string;
    onMouseDown: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onMouseUp: (event: React.MouseEvent<HTMLButtonElement>) => void;
    delay?: number;
}

const PtzButton = (props: IPtzButtonProps) => {
    const { children, className, onMouseUp, onMouseDown, delay = 2000 } = props;

    const [isPressed, setIsPressed] = useState<boolean>();

    const { onStart, onEnd } = usePTZDelayedEventHandlers({ delay });

    const handleMouseDown = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            setIsPressed(true);
            onMouseDown?.(e);
        },
        [onMouseDown]
    );

    const handleMouseUp = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            setIsPressed(false);
            onMouseUp?.(e);
        },
        [onMouseUp]
    );

    return (
        <IconButton
            className="opacity-70"
            color="secondary"
            onMouseDown={(e) => onStart(() => handleMouseDown(e))}
            onMouseUp={(e) => onEnd(() => handleMouseUp(e))}
        >
            {children}
        </IconButton>
    );
};

export default PtzButton;
